/**
 * Email Verification Page
 * Handles email verification callbacks from both hover and beta platforms
 */

import React, { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { emailVerificationService } from '../services/UnifiedEmailVerificationService';
import { betaAuthService } from '../services/BetaAuthService';

export const EmailVerification: React.FC = () => {
  const [, setLocation] = useLocation();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('Verifying your email...');
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // Get URL parameters
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token') || params.get('token_hash');
        const type = params.get('type') || 'email';
        const emailParam = params.get('email');
        const platform = params.get('platform');
        const next = params.get('next') || '/portal';

        if (!token) {
          setStatus('error');
          setMessage('Invalid verification link. Please request a new verification email.');
          return;
        }

        // Handle the verification
        const result = await emailVerificationService.handleVerificationCallback(params);

        if (result.success) {
          setStatus('success');
          setMessage(result.message || 'Email verified successfully!');
          setEmail(result.email || emailParam);

          // If this was a beta user, sync the verification status
          if (platform === 'beta' && result.email) {
            await betaAuthService.getUserJourneyStatus(result.email);
          }

          // Redirect after successful verification
          setTimeout(() => {
            setLocation(next);
          }, 3000);
        } else {
          setStatus('error');
          setMessage(result.message || 'Verification failed. Please try again.');
        }
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        setMessage('An error occurred during verification. Please try again.');
      }
    };

    verifyEmail();
  }, [setLocation]);

  const handleResendVerification = async () => {
    if (!email) return;

    setStatus('verifying');
    setMessage('Resending verification email...');

    const result = await emailVerificationService.resendVerificationEmail(email);
    
    if (result.success) {
      setStatus('success');
      setMessage('Verification email sent! Please check your inbox.');
    } else {
      setStatus('error');
      setMessage(result.message || 'Failed to resend verification email.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-black flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-purple-500/20">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            {status === 'verifying' && (
              <Loader2 className="w-16 h-16 text-purple-400 animate-spin" />
            )}
            {status === 'success' && (
              <CheckCircle className="w-16 h-16 text-green-400" />
            )}
            {status === 'error' && (
              <XCircle className="w-16 h-16 text-red-400" />
            )}
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-white text-center mb-4">
            Email Verification
          </h2>

          {/* Message */}
          <p className="text-purple-200 text-center mb-6">
            {message}
          </p>

          {/* Email display */}
          {email && status === 'success' && (
            <div className="bg-purple-900/30 rounded-lg p-3 mb-6">
              <div className="flex items-center justify-center space-x-2">
                <Mail className="w-5 h-5 text-purple-300" />
                <span className="text-purple-100">{email}</span>
              </div>
            </div>
          )}

          {/* Actions */}
          {status === 'error' && email && (
            <button
              onClick={handleResendVerification}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
            >
              Resend Verification Email
            </button>
          )}

          {status === 'success' && (
            <div className="text-center">
              <p className="text-purple-300 text-sm">
                Redirecting to your dashboard...
              </p>
              <button
                onClick={() => setLocation('/portal')}
                className="mt-4 text-purple-400 hover:text-purple-300 underline text-sm"
              >
                Click here if not redirected
              </button>
            </div>
          )}

          {/* Additional help */}
          <div className="mt-8 pt-6 border-t border-purple-500/20">
            <p className="text-purple-300 text-center text-sm">
              Having trouble? Contact{' '}
              <a href="mailto:support@redheli.com" className="text-purple-400 hover:text-purple-300 underline">
                support@redheli.com
              </a>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};